/**
 * Fusion Case Files — Workbench-style dossier for the last imported bank statement.
 * Shows holder, account #, Dr/Cr, counterparties, and ledger lines; offers
 * demo → real case promote when still on the /fusion sandbox.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle, Banknote, CheckCircle2, ExternalLink, FileText, FolderOpen, Loader2, Save,
} from 'lucide-react';
import { CaseSummary, type CaseStat } from '../cases/CaseSummary';
import { caseService } from '../../services/api';
import { useBankImportStore } from '../../stores/useBankImportStore';
import { useFusionStore } from '../../stores/useFusionStore';
import { useIntakeStore } from '../../stores/useIntakeStore';
import { promoteBankImportToCase } from '../../data/promoteBankImport';
import { isGuestDemo } from '../../utils/guestDemo';
import { statementFlow } from '../../services/statementFlow';

function inr(n: number) {
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
}

export function FusionCaseFiles() {
  const navigate = useNavigate();
  const dossier = useBankImportStore((s) => s.dossier);
  const bindLeCase = useBankImportStore((s) => s.bindLeCase);
  const setShowDemo = useFusionStore((s) => s.setShowDemo);
  const showDemo = useFusionStore((s) => s.showDemo);
  const intakeCaseId = useIntakeStore((s) => s.caseId);
  const guest = isGuestDemo();

  const [mode, setMode] = useState<'new' | 'existing'>('new');
  const [caseTitle, setCaseTitle] = useState('');
  const [firNumber, setFirNumber] = useState('');
  const [cases, setCases] = useState<Array<{ id: string; title: string }>>([]);
  const [selectedCaseId, setSelectedCaseId] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!dossier) return;
    const holder = (dossier.kyc?.holder_name || '').trim();
    if (holder && !caseTitle) setCaseTitle(`Bank statement — ${holder}`);
  }, [dossier, caseTitle]);

  useEffect(() => {
    if (guest || mode !== 'existing') return;
    let cancelled = false;
    caseService.getAll().then((list: unknown) => {
      if (cancelled) return;
      const rows = Array.isArray(list) ? list : (list as { cases?: unknown[] })?.cases || [];
      setCases(
        (rows as Array<{ id?: string; case_id?: string; title?: string }>)
          .map((c) => ({ id: String(c.id || c.case_id || ''), title: c.title || 'Untitled' }))
          .filter((c) => c.id),
      );
    }).catch(() => { if (!cancelled) setCases([]); });
    return () => { cancelled = true; };
  }, [guest, mode]);

  if (!dossier) {
    return (
      <div className="p-4 text-xs space-y-2" data-testid="fusion-case-files-empty">
        <div className="flex items-center gap-2 font-semibold text-sm">
          <FolderOpen size={15} /> Case files
        </div>
        <p className="text-muted-foreground leading-relaxed">
          Import a bank statement on the <span className="font-medium text-foreground">Bank import</span> tab.
          Holder, account number, transactions, counterparties, debits and credits land here — same dossier
          shape as Workbench case files.
        </p>
      </div>
    );
  }

  const kyc = dossier.kyc;
  const stats: CaseStat[] = [
    { label: 'Transactions', value: dossier.summary.txns },
    { label: 'Counterparties', value: dossier.summary.cps },
    { label: 'Debits', value: inr(dossier.summary.dr) },
    { label: 'Credits', value: inr(dossier.summary.cr) },
  ];

  const alreadyOnCase = !!(intakeCaseId || dossier.leCaseId);
  const boundCaseId = intakeCaseId || dossier.leCaseId;
  const deepLink = dossier.deepLink
    || (dossier.sfCaseId ? statementFlow.deepLink(dossier.sfCaseId, dossier.statementId) : undefined);

  const switchToMyData = () => {
    setShowDemo(false);
    setOkMsg('Demo seed hidden — canvas shows your imported statement only.');
  };

  const promote = async () => {
    if (guest) {
      setErr('Sign in to save this statement to a real case file.');
      return;
    }
    setBusy(true); setErr(null); setOkMsg(null);
    try {
      const { caseId } = await promoteBankImportToCase({
        dossier,
        mode,
        caseTitle,
        firNumber,
        existingCaseId: selectedCaseId || undefined,
      });
      bindLeCase(caseId);
      useIntakeStore.getState().setCaseId(caseId);
      setShowDemo(false);
      setOkMsg('Saved to case file. Opening Fusion Canvas…');
      navigate(`/cases/${caseId}/canvas`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-4 text-xs space-y-3" data-testid="fusion-case-files">
      <div className="flex items-center gap-2 font-semibold text-sm">
        <FolderOpen size={15} /> Case files
        <span className="ml-auto text-[10px] font-normal text-muted-foreground flex items-center gap-1">
          <Banknote size={12} /> Bank statement
        </span>
      </div>

      <div className="rounded border border-border bg-secondary/30 p-2.5 space-y-2">
        <div className="flex items-center gap-1.5 text-foreground font-medium text-[11px]">
          <FileText size={13} />
          {dossier.fileName || dossier.statement?.source_file_name || 'Imported statement'}
        </div>
        <CaseSummary stats={stats} layout="stack" />
        <div className="grid grid-cols-2 gap-1 text-[11px] border-t border-border pt-1.5">
          {kyc?.holder_name && (
            <><span className="text-muted-foreground">Holder</span>
              <span className="text-right font-medium break-all">{kyc.holder_name}</span></>
          )}
          {kyc?.account_number && (
            <><span className="text-muted-foreground">Account</span>
              <span className="text-right font-mono text-[10px]">{kyc.account_number}</span></>
          )}
          {kyc?.ifsc && (
            <><span className="text-muted-foreground">IFSC</span>
              <span className="text-right font-mono text-[10px]">{kyc.ifsc}</span></>
          )}
          {kyc?.bank && (
            <><span className="text-muted-foreground">Bank</span>
              <span className="text-right font-medium break-all">{kyc.bank}</span></>
          )}
          {kyc?.branch && (
            <><span className="text-muted-foreground">Branch</span>
              <span className="text-right font-medium break-all">{kyc.branch}</span></>
          )}
        </div>
        {dossier.statement?.narrative_summary && (
          <p className="text-[10px] text-muted-foreground border-t border-border pt-1.5">
            {dossier.statement.narrative_summary}
          </p>
        )}
      </div>

      {dossier.counterparties.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
            Counterparties · {dossier.counterparties.length}
          </div>
          <div className="space-y-1 max-h-36 overflow-y-auto">
            {dossier.counterparties.map((c) => (
              <div key={c.name} className="rounded border border-border bg-secondary/20 px-2 py-1.5 flex justify-between gap-2">
                <span className="truncate font-medium">{c.name}</span>
                <span className="shrink-0 text-muted-foreground tabular-nums">
                  {c.count} · {c.debit ? `Dr ${inr(c.debit)}` : ''}{c.debit && c.credit ? ' · ' : ''}{c.credit ? `Cr ${inr(c.credit)}` : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {dossier.transactions.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
            Transactions · {dossier.transactions.length}
          </div>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {dossier.transactions.map((t) => (
              <div key={t.id} className="rounded border border-border bg-secondary/40 px-2 py-1.5">
                <div className="flex items-center justify-between gap-2">
                  <span className={`text-[11px] font-semibold ${t.direction === 'in' ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {t.direction === 'in' ? '↓ Cr' : '↑ Dr'}
                    {t.value ? ` · ${t.value.toLocaleString('en-IN')} ${t.unit}` : ''}
                  </span>
                  <span className="text-[9px] text-muted-foreground">
                    {t.ts ? new Date(t.ts).toLocaleString() : ''}
                  </span>
                </div>
                <div className="text-[11px] truncate">{t.counterparty}</div>
                <div className="text-[9px] text-muted-foreground font-mono break-all">{t.ref}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {deepLink && (
        <a href={deepLink} target="_blank" rel="noreferrer"
          className="flex items-center justify-center gap-1.5 px-3 py-1.5 rounded border border-border bg-secondary hover:bg-secondary/70 text-foreground font-medium">
          <ExternalLink size={14} /> Full ledger in StatementFlow
        </a>
      )}

      {/* Demo → case mode */}
      {!guest && !alreadyOnCase && (
        <div className="rounded border border-primary/30 bg-primary/5 p-2.5 space-y-2">
          <div className="font-semibold text-[11px] text-foreground">Open as case file</div>
          <p className="text-[10px] text-muted-foreground leading-relaxed">
            Leave the Fusion demo sandbox and save this statement onto a real LE case — Evidence Locker
            + Fusion Canvas, like Workbench case files.
          </p>
          {showDemo && (
            <button type="button" onClick={switchToMyData}
              className="w-full text-[11px] px-2 py-1.5 rounded border border-border bg-secondary hover:bg-secondary/80">
              Hide demo seed first (My data only)
            </button>
          )}
          <div className="flex gap-2">
            <label className="flex items-center gap-1 cursor-pointer">
              <input type="radio" checked={mode === 'new'} onChange={() => setMode('new')} /> New case
            </label>
            <label className="flex items-center gap-1 cursor-pointer">
              <input type="radio" checked={mode === 'existing'} onChange={() => setMode('existing')} /> Existing case
            </label>
          </div>
          {mode === 'new' ? (
            <div className="space-y-1.5">
              <input
                value={caseTitle}
                onChange={(e) => setCaseTitle(e.target.value)}
                className="w-full text-[11px] px-2 py-1.5 bg-background border border-input rounded"
                placeholder="Case title"
                disabled={busy}
              />
              <input
                value={firNumber}
                onChange={(e) => setFirNumber(e.target.value)}
                className="w-full text-[11px] px-2 py-1.5 bg-background border border-input rounded"
                placeholder="FIR / Crime number (optional)"
                disabled={busy}
              />
            </div>
          ) : (
            <select
              value={selectedCaseId}
              onChange={(e) => setSelectedCaseId(e.target.value)}
              className="w-full text-[11px] px-2 py-1.5 bg-background border border-input rounded"
              disabled={busy}
            >
              <option value="">Select case…</option>
              {cases.map((c) => (
                <option key={c.id} value={c.id}>{c.title}</option>
              ))}
            </select>
          )}
          <button
            type="button"
            disabled={busy || (mode === 'existing' && !selectedCaseId) || (mode === 'new' && !caseTitle.trim())}
            onClick={() => void promote()}
            className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded bg-primary text-primary-foreground font-medium hover:bg-brand-gold-hover disabled:opacity-50"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {busy ? 'Saving…' : 'Save to case & open Fusion'}
          </button>
        </div>
      )}

      {alreadyOnCase && boundCaseId && (
        <div className="rounded border border-emerald-500/30 bg-emerald-500/5 p-2 text-[11px] flex items-start gap-1.5">
          <CheckCircle2 size={13} className="mt-0.5 text-emerald-600 shrink-0" />
          <div>
            Bound to case <span className="font-mono">{boundCaseId}</span>.
            {' '}
            <button type="button" className="underline" onClick={() => navigate(`/cases/${boundCaseId}`)}>
              Open case file
            </button>
            {' · '}
            <button type="button" className="underline" onClick={() => navigate(`/cases/${boundCaseId}/canvas`)}>
              Fusion Canvas
            </button>
          </div>
        </div>
      )}

      {guest && (
        <p className="text-[10px] text-muted-foreground">
          Guest demo — sign in to promote this statement into a real case file.
        </p>
      )}

      {okMsg && (
        <div className="text-[11px] text-emerald-600 flex items-start gap-1.5">
          <CheckCircle2 size={12} className="mt-0.5 shrink-0" /> {okMsg}
        </div>
      )}
      {err && (
        <div className="text-[11px] text-red-500 flex items-start gap-1.5">
          <AlertTriangle size={12} className="mt-0.5 shrink-0" /> {err}
        </div>
      )}
    </div>
  );
}
